import {IOmgExternalLinkHandler} from "./omg.external-link-handler.interface";
import {
    ExternalLinkInIframeHandlerBase,
    ExternalLinkInIframeRendererParams
} from "../external-link-in-iframe-handler-base";
import {GNWP_SOURCES} from "../../../../../sdk/src/components/external_links/external_links_consts";
import {ExternalLinksZIndexes} from "../../external-links-z-indexes";
import {IOpenMyAccountOptions} from "../my-account/my-account.external-link-handler.interface";
import {CIPropRead} from "../../../../../utils/case-insensitive-property-reader";
import {OpenCashierOptions, OpenDepositOptions} from "../../../../../common/interfaces/cashier.service.interface";
import {CHANNEL_SOURCE} from "../../../../../common/const/casinofied-analytics-consts";
import {AreaType} from "@sparkware/uc-sdk-core";
import {
    NullableUndefinedNumber,
    NullableUndefinedString
} from "../../../../../common/types/nullable-types";
import {IOpenOmgOptions} from "../../external-links.service.interface";
import {IOmgHandlerHook} from "./omg-handler.hook.interface";
import {CashierSessionTypes} from "../../../../../common/const/cashier-session-types";

const OMG_POST_ACTIONS = {
    SHOW_ME: 'ShowMe',
    CLOSE_ME: 'CloseMe',
    OPEN_REAL_GAME: '1',
    CASHIER: '6',
    QUICK_DEPOSIT: '7',
    MY_ACCOUNT: '21',
    SPORT: '24',
    EXTERNAL_LINK: '27',
    INTERNAL_URL: '32',
    OPEN_DEMO_GAME: '36',
    RICH: '56'
}

export class OmgExternalLinkHandler extends ExternalLinkInIframeHandlerBase implements IOmgExternalLinkHandler, IOmgHandlerHook {

    async openOmg(options: IOpenOmgOptions): Promise<void> {
        if(!this.services.user.isAuthorized) {
            this.services.logger.error(`Attempt to open OMG messages for unauthorized user`);
            return;
        }


        const url = await this._getGnwpUrl(options);
        await this._showExternalLinkDialog(url, {
            zIndex: options.zIndex
        });
    }

    protected async _getGnwpUrl(options: IOpenOmgOptions): Promise<string> {
        return await this.services.gnwp.getOmgUrl({
            openedWithinGame: options.openedWithinGame,
            messages: options.messages
        });
    }

    protected _getGnwpSource(): GNWP_SOURCES {
        return GNWP_SOURCES.OMG;
    }

    protected _getIframeRendererParams(): ExternalLinkInIframeRendererParams {
        return {
            maxWidth: 1024,
            maxHeight: 768,
            httpVerb: 'POST',
            zIndex: ExternalLinksZIndexes.OmgBehindTheGame,
            hideCloseButton: true,
            analyticsAction: this._getGnwpSource(),
            createAsHidden: true,
            iframeScrolling: this.services.device.os.isMobileOS ? "no" : "auto"
        };
    }

    protected async _onPostMessageReceived(message: MessageEvent): Promise<void> {
        const actionId = this._extractActionId(message);
        if(!actionId) {
            return;
        }

        switch (actionId) {
            case OMG_POST_ACTIONS.SHOW_ME:
                const showMeMessageData = this._parseMessageData(message);
                const width = CIPropRead.getNumericProperty("Width", showMeMessageData);
                const height = CIPropRead.getNumericProperty("Height", showMeMessageData);
                this._onShowMe(width, height);
                this.services.sdk.eventsTrigger.omgDisplayed(this);
                break;
            case OMG_POST_ACTIONS.CLOSE_ME:
                this._closeExternalLinkDialog();
                break;
            case OMG_POST_ACTIONS.MY_ACCOUNT:
                await this._openMyAccount({
                    tabId:  CIPropRead.getNumericProperty("TabId", this._parseMessageData(message)) ?? undefined
                })
                break;
            case OMG_POST_ACTIONS.CASHIER:
                const cashierMessageData = this._parseMessageData(message);
                await this._openCashier({
                    sessionType: CIPropRead.getNumericProperty('TabId', cashierMessageData) ?? undefined,
                    promCode: CIPropRead.getStringProperty('PromCode', cashierMessageData) ?? undefined
                });
                break;
            case OMG_POST_ACTIONS.QUICK_DEPOSIT:
                await this._openDeposit({
                    PromCode: CIPropRead.getStringProperty('PromCode', this._parseMessageData(message)) ?? undefined,
                    channel: {
                        source: CHANNEL_SOURCE.omg,
                        area: AreaType.OMG,
                        element: OMG_POST_ACTIONS.CASHIER
                    }
                });
                break;
            case OMG_POST_ACTIONS.OPEN_REAL_GAME:
                await this._openRealGame(CIPropRead.getNumericProperty('GameId', this._parseMessageData(message)) ?? undefined)
                break;
            case OMG_POST_ACTIONS.OPEN_DEMO_GAME:
                await this._openDemoGame(CIPropRead.getNumericProperty('GameId', this._parseMessageData(message)) ?? undefined)
                break;
            case OMG_POST_ACTIONS.SPORT:
                await this._openSport();
                break;
            case OMG_POST_ACTIONS.EXTERNAL_LINK:
                await this._openExternalUrl(this._extractUrl(message));
                break;
            case OMG_POST_ACTIONS.INTERNAL_URL:
                await this._openInternalUrl(this._extractUrl(message));
                break;

            case OMG_POST_ACTIONS.RICH:
                await this._openRichPage(CIPropRead.getStringProperty('campaignID', this._parseMessageData(message)));
                break;

            default:
                this.services.logger.debug(`Received unknown actionId ${actionId} from OMG`);
        }
    }


    protected _onShowMe(width: NullableUndefinedNumber, height: NullableUndefinedNumber): void {
        width = width ?? 0;
        height = height ?? 0;

        if(width > 0 && height > 0) {
            this._renderer?.setMaxSize(width, height);
        }
        this._renderer?.display();
    }

    protected async _openMyAccount(options?: IOpenMyAccountOptions): Promise<void> {
        this._closeExternalLinkDialog();
        await this.services.externalLinks.openMyAccount(options);
    }

    protected async _openCashier(options: {sessionType?: CashierSessionTypes, promCode?: NullableUndefinedString}): Promise<void> {
        this._closeExternalLinkDialog();

        const openCashierOptions: OpenCashierOptions = {
            channel: {
                source: CHANNEL_SOURCE.omg,
                area: AreaType.OMG,
                element: OMG_POST_ACTIONS.CASHIER
            }
        };

        if(options.promCode) {
            openCashierOptions.PromCode = options.promCode;
        }

        if(options.sessionType) {
            openCashierOptions.sessionType = options.sessionType;
        }

        await this.services.cashier.openCashier(openCashierOptions);
    }

    protected async _openDeposit(options: OpenDepositOptions): Promise<void> {
        this._closeExternalLinkDialog();

        const openDepositOptions: OpenDepositOptions = {
            channel: {
                source: CHANNEL_SOURCE.omg,
                area: AreaType.OMG,
                element: OMG_POST_ACTIONS.CASHIER
            }
        }

        if(options.PromCode) {
            openDepositOptions.PromCode = options.PromCode;
        }

        await this.services.cashier.openDeposit(openDepositOptions);
    }

    private async _openGame(gameType: NullableUndefinedNumber, isReal: boolean): Promise<void> {
        this._closeExternalLinkDialog();
        if(!gameType) {
            this.services.logger.error(`OMG open real game sent undefined game type`);
            return;
        }

        const gameInfo = await this.services.games.getGameInfo(gameType);

        if(!gameInfo) {
            this.services.logger.error(`OMG try to open ${isReal ? 'real' : 'demo'} game ${gameType} but this game is not in the layout`);
            return;
        }

        await gameInfo.open({
            isRealMode: isReal
        });
    }

    protected async _openRealGame(gameType: NullableUndefinedNumber): Promise<void> {
        await this._openGame(gameType, true);
    }

    protected async _openDemoGame(gameType: NullableUndefinedNumber): Promise<void> {
        await this._openGame(gameType, false);
    }

    protected async _openSport(): Promise<void> {
        this._closeExternalLinkDialog();
        await this.services.externalLinks.openSport();
    }

    protected async _openExternalUrl(externalUrl: NullableUndefinedString): Promise<void> {
        this._closeExternalLinkDialog();
        if(externalUrl) {
            await this.services.externalLinks.openExternalUrl(externalUrl);
        } else {
            this.services.logger.error('OMG redirect to external url failed because no URL provided');
        }


    }

    protected async _openInternalUrl(internalUrl: NullableUndefinedString): Promise<void> {
        this._closeExternalLinkDialog();
        if(internalUrl) {
            await this.services.externalLinks.openInternalUrl(internalUrl);
        } else {
            this.services.logger.error('OMG redirect to internal url failed because no URL provided');
        }

    }

    protected async _openRichPage(campaignId: NullableUndefinedString): Promise<void> {
        this._closeExternalLinkDialog();
        let richPageUrl = this.services.sdk.getRichPageUrl(campaignId);
        await this.services.location.push(richPageUrl);
    }

    bringToFront(): void {
        this._renderer?.updateZIndex(ExternalLinksZIndexes.Omg);
    }

}